import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Minus, Plus, ShoppingBag, ArrowLeft, Package } from 'lucide-react';
import { FREE_SHIPPING_THRESHOLD, getAmountUntilFreeShipping, getShippingCost } from '../utils/shipping';

const Cart = () => {
  const { items, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const handleQuantityChange = (productId, currentQuantity, change, maxStock = null) => {
    const newQuantity = currentQuantity + change;
    
    // Check stock limit if maxStock is provided
    if (maxStock !== null && newQuantity > maxStock) {
      alert(`Only ${maxStock} item(s) available in stock!`);
      return;
    }
    
    if (newQuantity >= 1) {
      updateQuantity(productId, newQuantity);
    } else if (newQuantity === 0) {
      removeFromCart(productId);
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }
    return `Rs. ${price.toLocaleString()}`;
  };

  const subtotal = getCartTotal();
  const shippingCost = getShippingCost(subtotal);
  const amountUntilFreeShipping = getAmountUntilFreeShipping(subtotal);
  const freeShippingProgress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

  const formatPrescriptionValue = (value) => {
    return value || 'null';
  };

  const renderPrescriptionDetails = (prescription, productType) => {
    if (!prescription) return null;

    const { rightEye, leftEye } = prescription;
    
    // Check if there's any prescription data based on product type
    let hasRightPrescription, hasLeftPrescription;
    
    if (productType === 'lenses') {
      // For lenses: only check SPH values
      hasRightPrescription = rightEye?.distance?.sph;
      hasLeftPrescription = leftEye?.distance?.sph;
    } else {
      // For eyeglasses: check all fields
      hasRightPrescription = rightEye?.distance?.sph || rightEye?.distance?.cyl || rightEye?.distance?.axis || 
                            rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis;
      hasLeftPrescription = leftEye?.distance?.sph || leftEye?.distance?.cyl || leftEye?.distance?.axis || 
                           leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis;
    }
    
    if (!hasRightPrescription && !hasLeftPrescription) return null;
    
    return (
      <div className="mt-2 text-sm text-gray-600">
        {/* Right Eye */}
        <div className="mb-2">
          <div className="font-medium">Prescription - Right:</div>
          {hasRightPrescription ? (
            <div className="ml-2">
              {(rightEye?.distance?.sph || (productType === 'eyeglasses' && (rightEye?.distance?.cyl || rightEye?.distance?.axis))) && (
                <div>
                  <div>Distance:</div>
                  <div className="ml-2">
                    {productType === 'lenses' ? (
                      `(SPH: ${formatPrescriptionValue(rightEye.distance.sph)})`
                    ) : (
                      `(SPH: ${formatPrescriptionValue(rightEye.distance.sph)}, CYL: ${formatPrescriptionValue(rightEye.distance.cyl)}, AXIS: ${formatPrescriptionValue(rightEye.distance.axis)})`
                    )}
                  </div>
                </div>
              )}
              {productType === 'eyeglasses' && (rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis) && (
                <div className="mt-1">
                  <div>Near:</div>
                  <div className="ml-2">(SPH: {formatPrescriptionValue(rightEye.near.sph)}, CYL: {formatPrescriptionValue(rightEye.near.cyl)}, AXIS: {formatPrescriptionValue(rightEye.near.axis)})</div>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2">
              <div>Distance:</div>
              <div className="ml-2">(SPH: )</div>
            </div>
          )}
        </div>

        {/* Left Eye */}
        <div>
          <div className="font-medium">Prescription - Left:</div>
          {hasLeftPrescription ? (
            <div className="ml-2">
              {(leftEye?.distance?.sph || (productType === 'eyeglasses' && (leftEye?.distance?.cyl || leftEye?.distance?.axis))) && (
                <div>
                  <div>Distance:</div>
                  <div className="ml-2">
                    {productType === 'lenses' ? (
                      `(SPH: ${formatPrescriptionValue(leftEye.distance.sph)})`
                    ) : (
                      `(SPH: ${formatPrescriptionValue(leftEye.distance.sph)}, CYL: ${formatPrescriptionValue(leftEye.distance.cyl)}, AXIS: ${formatPrescriptionValue(leftEye.distance.axis)})`
                    )}
                  </div>
                </div>
              )}
              {productType === 'eyeglasses' && (leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis) && (
                <div className="mt-1">
                  <div>Near:</div>
                  <div className="ml-2">(SPH: {formatPrescriptionValue(leftEye.near.sph)}, CYL: {formatPrescriptionValue(leftEye.near.cyl)}, AXIS: {formatPrescriptionValue(leftEye.near.axis)})</div>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2">
              <div>Distance:</div>
              <div className="ml-2">(SPH: )</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Shopping Cart - Mughal Ainak Mahal</title>
          <meta name="description" content="Your shopping cart at Mughal Ainak Mahal. View your selected eyewear items." />
        </Helmet>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-gray-400" />
            </div>
            <h1 className="text-3xl font-bold text-secondary mb-4">Your Cart is Empty</h1>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added any items to your cart yet. Start shopping to find your perfect eyewear!
            </p>
            <div className="space-y-4">
              <Link
                to="/eyeglasses"
                className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition duration-300"
              >
                Shop Eyeglasses
              </Link>
              <div className="flex justify-center space-x-4">
                <Link
                  to="/sunglasses"
                  className="inline-block bg-gray-200 text-secondary px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition duration-300"
                >
                  Sunglasses
                </Link>
                <Link
                  to="/lenses"
                  className="inline-block bg-gray-200 text-secondary px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition duration-300"
                >
                  Lenses
                </Link>
              </div>
              <Link
                to="/my-orders"
                className="inline-block bg-secondary text-white px-8 py-3 rounded-lg font-semibold hover:bg-secondary-dark transition duration-300 flex items-center justify-center mx-auto"
              >
                <Package className="w-5 h-5 mr-2" />
                My Orders
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Shopping Cart - Mughal Ainak Mahal</title>
        <meta name="description" content="Your shopping cart at Mughal Ainak Mahal. Review your selected eyewear items." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-primary transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Continue Shopping
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-secondary">Shopping Cart</h1>
          <button
            onClick={clearCart}
            className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-secondary">
                  Cart Items ({items.length})
                </h2>
              </div>
              
              {/* Cart Table Header */}
              <div className="hidden md:grid md:grid-cols-4 bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-700 border-b border-gray-200">
                <div>Product</div>
                <div>Price</div>
                <div>Quantity</div>
                <div>Total</div>
              </div>
              
              <div className="divide-y divide-gray-200">
                {items.map((item) => (
                  <div key={item.id} className="p-6">
                    {/* Desktop Layout */}
                    <div className="hidden md:grid md:grid-cols-4 gap-4 items-start">
                      {/* Product Column */}
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-secondary mb-1">
                            {item.name}
                          </h3>
                          {renderPrescriptionDetails(item.prescription, item.productType)}
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-600 hover:text-red-700 text-sm mt-2 transition-colors duration-200"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Price Column */}
                      <div className="text-sm font-semibold text-secondary">
                        {formatPrice(item.price)}
                      </div>

                      {/* Quantity Column */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-semibold w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                          className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Total Column */}
                      <div className="text-sm font-bold text-primary">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-secondary mb-1">
                            {item.name}
                          </h3>
                          <p className="text-primary font-bold text-sm">
                            {formatPrice(item.price)}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-600 hover:text-red-700 transition-colors duration-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {renderPrescriptionDetails(item.prescription, item.productType)}
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                            className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-lg font-semibold w-12 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                            className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="text-sm font-bold text-primary">
                          Total: {formatPrice(item.price * item.quantity)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-secondary mb-6">Order Summary</h2>
              <div className="mb-6 rounded-lg bg-gray-50 p-4">
                <div className="mb-2 flex justify-between text-sm">
                  <span className="font-medium text-secondary">
                    {shippingCost === 0 ? 'Free delivery unlocked' : 'Free delivery'}
                  </span>
                  <span className="font-semibold text-primary">
                    {shippingCost === 0 ? 'Free' : `Rs. ${FREE_SHIPPING_THRESHOLD.toLocaleString()}+`}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${freeShippingProgress}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  {shippingCost === 0
                    ? 'Your order qualifies for free delivery.'
                    : `Spend ${formatPrice(amountUntilFreeShipping)} more for free delivery.`}
                </p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">Rs. {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold">{shippingCost === 0 ? 'Free' : `Rs. ${shippingCost.toLocaleString()}`}</span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-secondary">Total</span>
                    <span className="text-lg font-bold text-primary">
                      Rs. {(subtotal + shippingCost).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-dark transition-colors duration-200"
                >
                  Proceed to Checkout
                </button>
                <Link
                  to="/my-orders"
                  className="flex items-center justify-center w-full bg-gray-200 text-secondary py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200"
                >
                  <Package className="w-5 h-5 mr-2" />
                  My Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 
